import { PIPE_WIDTH } from "../../config";

// Vertical bob applied to night pipes (radians/sec). Amplitude is set per pipe at
// spawn (0 for static day pipes) so a pipe never changes behaviour mid-flight.
const OSC_FREQ = 2.0;

export interface AABB {
  x: number;
  y: number;
  w: number;
  h: number;
}

export class Pipe {
  x = 0;
  gapY = 0;
  gapHeight = 0;
  passed = false;
  active = false;
  /** Vertical-bob amplitude (px); 0 = static. Set by PipePool at spawn. */
  amp = 0;
  private phase = 0;
  private oscY = 0;

  reset(x: number, gapY: number, gapHeight: number): void {
    this.x = x;
    this.gapY = gapY;
    this.gapHeight = gapHeight;
    this.passed = false;
    this.active = true;
    this.amp = 0;
    this.phase = 0;
    this.oscY = 0;
  }

  /** Start a vertical bob at the given amplitude and initial phase (night pipes). */
  beginBob(amp: number, phase: number): void {
    this.amp = amp;
    this.phase = phase;
    this.oscY = Math.sin(phase) * amp;
  }

  /** The gap centre including the current bob offset (drives both draw + collision). */
  private centre(): number {
    return this.gapY + this.oscY;
  }

  topRect(): AABB {
    const c = this.centre();
    return {
      x: this.x,
      y: 0,
      w: PIPE_WIDTH,
      h: c - this.gapHeight / 2,
    };
  }

  bottomRect(groundY: number): AABB {
    const top = this.centre() + this.gapHeight / 2;
    return {
      x: this.x,
      y: top,
      w: PIPE_WIDTH,
      h: groundY - top,
    };
  }

  update(dt: number, scrollSpeed: number): void {
    if (!this.active) return;
    this.x -= scrollSpeed * dt;
    if (this.amp !== 0) {
      this.phase += dt * OSC_FREQ;
      this.oscY = Math.sin(this.phase) * this.amp;
    }
  }

  render(ctx: CanvasRenderingContext2D, groundY: number): void {
    if (!this.active) return;
    const top = this.topRect();
    const bot = this.bottomRect(groundY);

    drawPineTrunk(ctx, top.x, top.y, top.w, top.h, true);
    drawPineTrunk(ctx, bot.x, bot.y, bot.w, bot.h, false);
  }
}

function drawPineTrunk(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  topColumn: boolean,
): void {
  // bark base
  ctx.fillStyle = "#5a3a22";
  ctx.fillRect(x, y, w, h);

  // shadow side
  ctx.fillStyle = "#3f2614";
  ctx.fillRect(x + w - 8, y, 8, h);

  // highlight side
  ctx.fillStyle = "#7a5232";
  ctx.fillRect(x + 1, y, 4, h);

  // bark grooves — vertical streaks
  ctx.fillStyle = "#3f2614";
  for (let gx = x + 8; gx < x + w - 10; gx += 9) {
    ctx.fillRect(gx, y, 1, h);
  }

  // ring at the cut end so the trunk reads as a clean cross-section
  const ringH = 6;
  const ringY = topColumn ? y + h - ringH : y;
  ctx.fillStyle = "#3f2614";
  ctx.fillRect(x, ringY, w, ringH);
  ctx.fillStyle = "#7a5232";
  ctx.fillRect(x, ringY + ringH - 2, w, 2);
}
